import {Flex, IconWithText, Text, Theme as ThemeWithoutType} from 'exsportia-components'
import {DateTime} from 'luxon'
import {FC, ReactNode} from 'react'
import {Params, useParams} from 'react-router-dom'

import {GetBookingQueryReturnType} from '../../../api/booking/get-booking'
import {hasPayableParticipant} from '../../../api/booking/helpers'
import {BookingUsageDataType} from '../../../api/booking/types'
import {BookingCard} from '../../../components/booking-card/booking-card'
import {BookingCode} from '../../../components/booking-code/booking-code'
import {TranslatorScope} from '../../../locale/types'
import {ThemeType} from '../../../theme/types'
import {Button} from '../../../ui/components/button/button'
import {LabeledValueRow} from '../../../ui/components/key-value-row/labeled-value-row'
import {PaymentStatus} from '../../../ui/components/payment-status/payment-status'
import {TitledSection} from '../../../ui/components/titeled-section/titled-section'
import {WBA_URL} from '../../../utils/environment'
import {auth} from '../../../utils/firebase'
import {LocaleOptions, useLocale} from '../../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../../utils/logic'
import {useModalActions} from '../../../utils/modals'
import {HelpChoice} from '../../contact-us/help-choice'
import {useSplitPaymentExpireDate} from '../hooks/split-payment-expire-date.hook'

export type BookingSectionProps = {
  booking: NonNullable<GetBookingQueryReturnType>
  isLoading: boolean
  handlePay: () => void
  paymentStatus: 'cancelled' | 'paid' | 'partiallyPaid' | 'unpaid'
  withEquipments: boolean
  bookingDuration: string
  bookingDateTime: DateTime<false> | DateTime<true>
  isBookingLoading: boolean
  handleCancelBooking: () => void
  openParticipantsModal: () => void
  handleOpenContactsModal: () => void
  // Shown just above the Cancel button (the Rain check card); nothing when absent.
  aboveCancel?: ReactNode
  getUsageString: (
    l: (scope: TranslatorScope, options?: LocaleOptions) => string,
    usage: BookingUsageDataType,
    isRefund: boolean,
  ) => string | null
}

export const BookingSection: FC<BookingSectionProps> = ({
  booking,
  isLoading,
  handlePay,
  paymentStatus,
  withEquipments,
  getUsageString,
  bookingDuration,
  bookingDateTime,
  isBookingLoading,
  handleCancelBooking,
  openParticipantsModal,
  handleOpenContactsModal,
  aboveCancel,
}) => {
  const {l} = useLocale()
  const {openModal, closeModal} = useModalActions()
  const openHelpChoice = () =>
    openModal({
      title: l('get_help_title'),
      component: (
        <HelpChoice
          onVenue={() => {
            closeModal()
            handleOpenContactsModal()
          }}
          onTechnical={() => {
            closeModal()
            window.open('https://wa.me/+66948951432', '_blank', 'noopener,noreferrer')
          }}
        />
      ),
      footerConfig: {disabled: true},
    })
  const params = useParams<Params>()
  const userId = auth.currentUser?.uid
  const Theme = ThemeWithoutType as ThemeType
  const {componentMarkup} = useSplitPaymentExpireDate({
    handlePay,
    userId,
    booking,
    isLoading,
    splitPaymentExpireDate: booking.splitPaymentExpireDate
      ? booking.splitPaymentExpireDate
      : Number(booking.date_timestamp),
  })

  return (
    <>
      <BookingCard
        duration={bookingDuration}
        bookingCode={booking.reference}
        qrLink={`${WBA_URL}/${booking.venueId}/booking/${params.id}`}
        dateAndTime={bookingDateTime.toUTC().toFormat('MMM dd, HH:mm')}
      />
      {(!booking.isCancelled && booking.splitPayment && !booking.isPaid) && (
        componentMarkup
      )}
      <TitledSection title={l('booking')}>
        <LabeledValueRow label={l('dateAndTime')}>
          {bookingDateTime.toUTC().toFormat('HH:mm, MMM dd, yyyy')}
        </LabeledValueRow>
        <LabeledValueRow label={l('duration')}>{bookingDuration}</LabeledValueRow>
        <LabeledValueRow label={l('participants')}>
          <Flex
            cursor='pointer'
            onClick={openParticipantsModal}
          >
            {`${booking.participants.length} ${l('participants')}`}
          </Flex>
        </LabeledValueRow>
        <LabeledValueRow label={l('headers_paymentStatus')}>
          <PaymentStatus status={paymentStatus} />
        </LabeledValueRow>
        {paymentStatus === 'cancelled' && (booking.refund || 0) > 0 && (
          <LabeledValueRow label={l('refunded', {caseAction: 'capitalize'})}>
            {l(`${booking.venue.currency}_symbolic` as TranslatorScope, {value: booking.refund})}
          </LabeledValueRow>
        )}
        {isNotNilAndNotEmpty(booking.usageData) && booking.occupyUsage === false && paymentStatus === 'cancelled' && (
          <LabeledValueRow label={l('usage')}>
            <Text settings={{color: Theme.colors.notify}}>{getUsageString(l, booking.usageData, true)}</Text>
          </LabeledValueRow>
        )}
        <LabeledValueRow label={l('equipments')}>{l(withEquipments ? 'yes' : 'no')}</LabeledValueRow>
        <LabeledValueRow label={l('reference')}>
          <BookingCode code={booking.reference} />
        </LabeledValueRow>
        <Flex width='min-content'>
          <Button
            type='flat'
            onClick={handleOpenContactsModal}
          >
            <IconWithText
              text={l('contactVenue', {caseAction: 'capitalize'})}
              icon='phone'
            />
          </Button>
        </Flex>
        <Flex width='min-content'>
          <Button
            type='flat'
            onClick={openHelpChoice}
          >
            <IconWithText
              text={l('get_help', {caseAction: 'capitalize'})}
              icon='help'
            />
          </Button>
        </Flex>
        {aboveCancel}
        <Flex
          mt={Theme.spaces.multiplier3x}
          gap={Theme.spaces.multiplier2x}
          flexDirection='column'
        >
          {booking.paymentOnExsportia && (!booking.splitPayment || hasPayableParticipant(booking.participants)) && (
            <Button
              type='primary'
              onClick={handlePay}
              disabled={booking.isCancelled || booking.isPaid}
              isLoading={isBookingLoading || isLoading}
            >
              {l('payNow')}
            </Button>
          )}
          <Button
            type='cancel'
            onClick={handleCancelBooking}
            disabled={booking.isCancelled}
            isLoading={isBookingLoading || isLoading}
          >
            {l('cancelBooking')}
          </Button>
        </Flex>
      </TitledSection>
    </>
  )
}
